import React, { useEffect, useCallback, useState } from "react";
import styled from "styled-components";
import GridTable from "./gridTable";
import Title from "./title";
import PaginationNav from "./paginationNav";
import data from "services/data";
import ToolsBar from "./toolsBar";
import logger from "utils/logger";
import PluginInfoModal from "./modals/pluginInfoModal";
import ConfirmModal from "./modals/confirmModal";
import { useHistory } from "react-router-dom";
import guide from "services/guide";
import notifier from "utils/notifier";
import { Modal,Table } from 'antd';

const StyledPluginInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;

const StyledMain = styled.main`
  background-color: ${({ theme }) => theme.secondBackgroundColor};
  box-shadow: ${({ theme }) => theme.boxShadow};
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 50px;
`;

const StyledSection = styled.section`
  width: 100%;
  min-height: 430px;
  word-wrap: break-word;
  text-align: justify;

  display: flex;
  flex-direction: column;
  row-gap: 10px;
`;

const tablePageSize = 18;

const PluginInfo = ({ user }) => {
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [items, setItems] = useState([]);
  const [modalVisible, setModalVisible] = useState({
    pluginInfoModal: false,
    confirmModal: false,
  });
  const [modalType, setModalType] = useState("");
  const [currentPlugin, setCurrentPlugin] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  const closeAllModal = useCallback(() => {
    setModalVisible({});
    // 清空错误信息
    setErrorMessage("");
  }, []);

  const handleCurrentPageChange = useCallback((pageNum) => {
    setCurrentPage(pageNum);
  }, []);

  const updateItems = useCallback(
    async (pageSize, currentPage) => {
      const { totalCount, pluginList } = await data.getPluginInfoList(
        pageSize,
        currentPage,
        keyword
      );
      setItems(pluginList);
      setTotal(totalCount);
    },
    [keyword]
  );

  const handleSearch = useCallback(() => {
    if (currentPage === 1) {
      updateItems(tablePageSize, 1);
    } else {
      setCurrentPage(1);
    }
  }, [currentPage, updateItems]);

  // 根据计算出的页数 来更新
  const updateItemsByCount = useCallback(
    (count) => {
      // 当总页数不为当前页 并且总页数大于0时 更新列表
      if (count !== currentPage && count > 0) {
        //跳到最后一页
        setCurrentPage(count);
      } else {
        // 获取和根据currentPage更新列表
        updateItems(tablePageSize, count);
      }
    },
    [currentPage, updateItems]
  );

  // 添加插件
  const handlePluginAdd = useCallback(
    async (form) => {
      try {
        const addPluginData = await data.addPlugin(form);
        // 计算共有多少页
        const count = Math.ceil((total + 1) / tablePageSize);
        // logger.log(total, tablePageSize, count);
        // 更新列表
        updateItemsByCount(count);
        // 关闭modal
        closeAllModal();
        return true;
      } catch (error) {
        // setErrorMessage(error.message);
        notifier.error(error.message);
      }
    },
    [closeAllModal, total, updateItemsByCount]
  );

  // 更新插件
  const handlePluginUpdate = useCallback(
    async (targetAppId, form) => {
      try {
        const updatePluginData = await data.updatePlugin(targetAppId, form);
        // logger.log(total, tablePageSize, count);
        // 更新列表
        updateItemsByCount(currentPage);
        // 关闭modal
        closeAllModal();
        return true;
      } catch (error) {
        // setErrorMessage(error.message);
        notifier.error(error.message);
      }
    },
    [closeAllModal, currentPage, updateItemsByCount]
  );

  // 删除插件
  const handlePluginDelete = useCallback(async () => {
    if (!currentPlugin) return;
    try {
      const deletePluginData = await data.deletePlugin(currentPlugin?.appId);
      // 更新列表
      updateItemsByCount(currentPage);
      // 关闭modal
      closeAllModal();
    } catch (error) {
      // setErrorMessage(error.message);
      notifier.error(error.message);
    }
  }, [closeAllModal, currentPage, currentPlugin, updateItemsByCount]);

  useEffect(() => {
    // 获取和根据currentPage更新列表
    updateItems(tablePageSize, currentPage);
  }, [currentPage]);


  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const getNoVerList = async () => {
   const res = await data.queryPluginNoVer()
   if(res?.payload && res?.payload?.plugin_list) {
    setDataSource(res?.payload?.plugin_list)
   }else {
    setDataSource([])
   }
  }

  const columns = [
    {
      title: 'id',
      dataIndex: 'app_id',
      key: 'app_id',
    },
    {
      title: '模块',
      dataIndex: 'module',
      key: 'module',
    },
    {
      title: '名称',
      dataIndex: 'alias_name',
      key: 'alias_name',
    },
   
    {
      title: '描述',
      dataIndex: 'info',
      key: 'info',
    },
    {
      title: '下载路径',
      dataIndex: 'install_path',
      key: 'install_path',
    },
  ];

  return (
    <StyledPluginInfo>
      <Modal title="未发布版本插件" visible={isModalOpen} onOk={handleOk} onCancel={handleCancel} width={1000} maskClosable={false}>
        <Table dataSource={dataSource} columns={columns} rowKey="app_id"/>
      </Modal>
      <StyledMain>
        <Title title="插件列表" />
        <StyledSection>
          <ToolsBar
            auth={!!user}
            hasButton = {true}
            buttonContent={'未发布版本插件'}
            tools={[
              {
                type: "add",
                size: "lg",
                onClick: () => {
                  setModalType("add");
                  setModalVisible({ pluginInfoModal: true });
                },
              },
            ]}
            search={{
              value: keyword,
              setValue: setKeyword,
              onSearch: handleSearch,
            }}
            // 点击上传插件按钮之后的处理函数
            handleButtonClick = {() => {
              // 弹窗显示未发布版本的插件
              showModal()
              getNoVerList()
            }}
          />
          <GridTable
            mode="normal"
            items={items}
            heads={["名称", "别名", "安装路径", "简介"]}
            paths={["module", "aliasName", "installPath", "info"]}
            showTools={!!user}
            auth={!!user}
            tools={[
              {
                type: "update",
                onClick: (data) => {
                  setModalType("update");
                  setModalVisible({ pluginInfoModal: true });
                  logger.log("点击了修改 当前点击的插件：", data);
                  setCurrentPlugin(data);
                },
              },
              {
                type: "remove",
                onClick: (data) => {
                  setModalType("");
                  setModalVisible({ confirmModal: true });
                  logger.log("点击了删除 当前点击的插件：", data);
                  setCurrentPlugin(data);
                },
              },
            ]}
            rowCursor="pointer"
            rowClick={(data) => {
              logger.log("这行的插件信息：", data);
              history.push({
                pathname: guide.pluginVerPages.releases.basePath,
                search: `appId=${encodeURIComponent(data.appId)}`,
              });
            }}
          />
        </StyledSection>
      </StyledMain>
      <PaginationNav
        total={total}
        pageSize={tablePageSize}
        currentPage={currentPage}
        onCurrentPageChange={handleCurrentPageChange}
      />
      <PluginInfoModal
        errorMessage={errorMessage}
        visible={modalVisible.pluginInfoModal}
        type={modalType}
        displayTargetPlugin
        data={currentPlugin}
        onClose={closeAllModal}
        onPluginAdd={handlePluginAdd}
        onPluginUpdate={handlePluginUpdate}
      />
      <ConfirmModal
        errorMessage={errorMessage}
        visible={modalVisible.confirmModal}
        onClose={closeAllModal}
        title="提示"
        content="确定要删除该插件？"
        confirmLabel="删除"
        cancelLabel="取消"
        confirmType="danger"
        onConfirm={handlePluginDelete}
        onCancel={closeAllModal}
      />
    </StyledPluginInfo>
  );
};

export default PluginInfo;
